<script>
  import { onMount, getContext } from 'svelte';
  import { goto } from '$app/navigation';
  import { format_date } from '#lib/util.js';
  import { format_price, swipe_navigation, extract_price_info, format_price_info } from '#lib/patch_util.js';
  import ButtonLabel from '#lib/ButtonLabel.svelte';
  import BlockGrid from '#lib/BlockGrid.svelte';
  import MonthSelector from '#lib/MonthSelector.svelte';
  import RollingNumberDisplay from '#lib/RollingNumberDisplay.svelte';
  import Icon from '#lib/Icon.svelte';
  import t from '#lib/i18n.svelte.js';
  import Alert from '#lib/Alert.svelte';
  import { slide } from 'svelte/transition';
  import Tooltip from '#lib/Tooltip.svelte';
  import AccountSwitcher from '#lib/AccountSwitcher.svelte';
  import Dialog from '#lib/Dialog.svelte';
  import { toast } from '#lib/toast.svelte.js';
  import Popover from '#lib/Popover.svelte';
  import BookingSheet from '#lib/BookingSheet.svelte';
  import { BookingSelection, signed_price, storno_count, sheet_labels, billing_line, receipt_lines } from '#lib/booking_selection.svelte.js';

  let { data } = $props();

  let popover_refs = $state({});

  let site_settings = getContext('site_settings');

  let current_user_can_book = $derived(
    data.current_user?.role === 'admin' 
    // also admins when impersonating another user can make bookings on their behalf (bypassing the payment method check)
    || data.authenticated_user?.role === 'admin' 
    || (
      data.current_user?.has_stripe_payment_method === 1 
      // For now we don't require a billing address, but we might in the future
      // && data.current_user?.has_stripe_billing_address === 1
    )
  );
  
  // =============================================================================
  // SELECTION: + and − change a pending count per day; nothing is booked or cancelled
  // until the member confirms in the sheet
  // =============================================================================

  /** Items: date → delta, positive adds desks, negative cancels own ones */
  const selection = new BookingSelection({
    api: '/api/apply-daily-booking-changes',
    resource_id: () => data.resource.entry_id,
    changes: items => [...items].map(([booking_date, delta]) => ({ booking_date, delta })),
    error_message,
    owner: () => `${data.current_user?.user_id}|${data.resource.entry_id}`,
    still_selectable: (date, delta) => {
      const slot = data.slots.find(s => s.date === date);
      if (!slot) return true;
      if (slot.blocked) return false;
      return delta > 0 ? slot.bookings_total + delta <= available_quantity : slot.bookings_by_current_user + delta >= 0;
    },
    // Date-specific refusals, including member limits, also in months that are not shown
    failed_items: ({ error, change }) => ([
      'NOT_ALLOWED',
      'MAX_INDIVIDUAL_DAILY_BOOKINGS_PER_DAY_REACHED',
      'MAX_INDIVIDUAL_DAILY_BOOKINGS_PER_MONTH_REACHED',
    ].includes(error)
      ? { keys: [change.booking_date], label: format_date(change.booking_date, 'EEEdMMM') }
      : null),
    on_refused,
  });

  const delta_of = date => selection.items.get(date) ?? 0;
  // The member's desks on a day once the selection is confirmed
  const projected_own = slot => slot.bookings_by_current_user + delta_of(slot.date);

  // The limit a further desk would exceed (admins are not limited)
  function booking_limit_message(slot) {
    if (data.authenticated_user?.role === 'admin') return null;
    const max_per_day = data.resource.max_individual_daily_bookings_per_day;
    const max_per_month = data.resource.max_individual_daily_bookings_per_month;
    if (max_per_day && projected_own(slot) >= max_per_day) {
      return t('max_individual_daily_bookings_per_day_reached', { max: max_per_day });
    }
    if (max_per_month && data.slots.reduce((total, s) => total + projected_own(s), 0) >= max_per_month) {
      return t('max_individual_daily_bookings_per_month_reached', { max: max_per_month });
    }
    return null;
  }

  const has_free_desk = slot => slot.bookings_total + delta_of(slot.date) < available_quantity;
  const can_decrease = slot => projected_own(slot) > 0;

  function change_selection(slot, step) {
    if (step > 0) {
      if (!data.current_user) {
        goto(`/login?return_to=book/${data.resource.slug}-${data.resource.entry_id}`);
        return;
      }
      // Taking back a pending cancellation needs no payment method or free desk
      if (delta_of(slot.date) >= 0) {
        if (!current_user_can_book) {
          show_add_missing_payment_details_toast();
          return;
        }
        const limit = booking_limit_message(slot);
        if (limit) {
          toast.error(limit, { auto_dismiss: true, show_close_icon: true, id: 'daily-booking-limit-reached' });
          return;
        }
        if (!has_free_desk(slot)) return;
      }
    } else if (!can_decrease(slot)) {
      return;
    }
    const delta = delta_of(slot.date) + step;
    const next = new Map(selection.items);
    if (delta === 0) next.delete(slot.date);
    else next.set(slot.date, delta);
    selection.items = next;
    selection.changed();
  }

  let summary = $derived.by(() => {
    if (!selection.items.size) return null;
    const { estimate } = selection;
    const entries = [...selection.items].sort(([a], [b]) => (a < b ? -1 : 1));
    const n_add = entries.reduce((n, [, delta]) => n + Math.max(delta, 0), 0);
    const n_remove = entries.reduce((n, [, delta]) => n + Math.max(-delta, 0), 0);
    const unit = count => t(count === 1 ? 'booking_unit_desk' : 'booking_unit_desks');
    const where = entries.length === 1 ? format_date(entries[0][0], 'EEEdMMM') : t('booking_days', { count: entries.length });
    const price_note = data.pricing
      ? data.pricing.max_price >= data.pricing.price
        ? `${t('booking_estimate_month_max', { price: format_price(data.pricing.price), max: format_price(data.pricing.max_price) })}${estimate?.capped ? ` ${t('booking_cap_reached')}` : ''}`
        : t('booking_estimate_day_price', { price: format_price(data.pricing.price) })
      : null;

    return {
      count: n_add || n_remove,
      unit: n_add ? unit(n_add) : t(n_remove === 1 ? 'booking_unit_desk_cancel' : 'booking_unit_desks_cancel'),
      price: n_add && estimate ? signed_price(estimate.amount) : null,
      sub: n_add && n_remove ? `${where} · ${storno_count(n_remove)}` : where,
      sub_warn: false,
      resource: data.resource.title,
      groups: entries.map(([date, delta]) => ({
        date,
        label: format_date(date, 'EEEEdMMMM'),
        rows: [delta > 0
          ? { kind: 'add', time: `${delta} ${unit(delta)}`, meta: '' }
          : { kind: 'remove', time: `${-delta} ${unit(-delta)}`, meta: t('booking_cancellation_free') }],
      })),
      estimate: n_add && estimate ? { total: signed_price(estimate.amount), note: [t('plus_vat'), price_note].filter(Boolean).join(' · ') } : null,
      deadline: null,
      billing: billing_line(data.current_user),
      ...sheet_labels(n_add, n_remove, unit),
    };
  });

  function error_message({ error }) {
    switch (error) {
      case 'MAX_INDIVIDUAL_DAILY_BOOKINGS_PER_DAY_REACHED':
        return t('max_individual_daily_bookings_per_day_reached', { max: data.resource.max_individual_daily_bookings_per_day });
      case 'MAX_INDIVIDUAL_DAILY_BOOKINGS_PER_MONTH_REACHED':
        return t('max_individual_daily_bookings_per_month_reached', { max: data.resource.max_individual_daily_bookings_per_month });
      case 'NOT_ALLOWED': return t('booking_error_changed');
    }
  }

  function on_refused(response) {
    // The session has ended
    if (response.error === 'NOT_AUTHORIZED') {
      goto(`/login?return_to=book/${data.resource.slug}-${data.resource.entry_id}`);
    } else if (response.error === 'NO_PAYMENT_METHOD_SET') {
      show_add_missing_payment_details_toast();
    }
  }

  function commit() {
    if (!summary) return;
    selection.commit({ title: summary.receipt_title, lines: receipt_lines(summary.groups), email: data.current_user.email });
  }

  let dialog = $state();

  function on_close_dialog() {
    dialog = undefined;
  }

  let available_quantity = $derived(data.pricing?.available_quantity || 0);

  // UI State
  let show_month_selector = $state(false);
  let expand_resource_booking_details = $state(false);

  onMount(() => {
    // Check if the instructions dialog has already been shown. A new key since booking needs a
    // confirmation (2026-09): members who knew one-tap booking see the instructions once more.
    const daily_booking_instructions_dialog_shown = localStorage.getItem('daily_booking_confirm_instructions_shown');
    if (!daily_booking_instructions_dialog_shown && data.current_user) {
      dialog = { mode: 'instructions' };
      localStorage.setItem('daily_booking_confirm_instructions_shown', 'true');
    }
  });

  function show_add_missing_payment_details_toast() {
    toast.info(
      t('add_missing_payment_details', {href: '/account/billing'}),
      {
        auto_dismiss: true,
        show_close_icon: true,
        id: 'add-missing-payment-details',
      }
    );
  }


  function navigate_adjacent_months(direction) {
    let year = data.year;
    let month = data.month;
    
    if (direction === 'back') {
      if (month === 1) {
        year--;
        month = 12;
      } else {
        month--;
      }
    } else {
      if (month === 12) {
        year++;
        month = 1;
      } else {
        month++;
      }
    }
    
    goto(`?year=${year}&month=${month}`, { replace: true, reset: false });
  }

  function handle_month_select({ month, year }) {
    show_month_selector = false;
    goto(`?year=${year}&month=${month + 1}`, { replace: true, reset: false });
  }

  function generate_desk_allocation_summary(slots) {
    // Create a map of date -> number of bookings
    const bookings_by_date = slots.reduce((acc, slot) => {
      if (slot.bookings_by_current_user > 0) {
        acc[slot.date] = slot.bookings_by_current_user;
      }
      return acc;
    }, {});

    // Check if there are any bookings
    const booking_values = Object.values(bookings_by_date);
    if (booking_values.length === 0) {
      return {
        total_desks: 0,
        desk_summaries: []
      };
    }

    // Find maximum number of concurrent desks
    const max_concurrent_desks = Math.max(...booking_values);

    // Generate desk summaries
    const desk_summaries = [];
    for (let desk_num = 1; desk_num <= max_concurrent_desks; desk_num++) {
      // Find dates where this desk number was used
      const desk_dates = Object.entries(bookings_by_date)
        .filter(([_, count]) => count >= desk_num)
        .map(([date]) => date)
        .sort();

      // Group consecutive dates
      const date_ranges = desk_dates.reduce((ranges, date) => {
        const last_range = ranges[ranges.length - 1];
        if (!last_range || new Date(date) - new Date(last_range.end) > 86400000) { // More than 1 day gap
          ranges.push({ start: date, end: date, days: 1 });
        } else {
          last_range.end = date;
          last_range.days++;
        }
        return ranges;
      }, []);

      desk_summaries.push({
        desk_number: desk_num,
        total_days: desk_dates.length,
        date_ranges
      });
    }

    return {
      total_desks: max_concurrent_desks,
      desk_summaries
    };
  }

  let desk_allocation_summary = $derived(
    generate_desk_allocation_summary(data.slots)
  );
</script>


<!-- 
# TODOs for Daily Booking Component
!!!!!!!!!!!!!!!!!!!!!

- For any bookings: users can cancel within 5minutes after booking 
- For next day bookings: users can only cancel until 14:00 (???)
- For bookings after tomorrow: users can cancel anytime

!!!!!!!!!!!!!!!!!!

## Navigation
- [ ] Add weekday legend
- [ ] Add month selector (same as in hourly booking UI)
- [ ] Add next/ prev month buttons
- [ ] Highlight current month
- [ ] Highlight current day


## Summary of bookings of a selected month (same as in hourly booking UI)
- [x] booking count per desk
- [ ] price per desk
- [ ] total cost per month


## Slot states
- [ ] Fully Booked with **no** booking by current user
- [ ] Fully Booked with booking by current user
- [ ] Blocked
- [ ] No Bookings
- [ ] Bookings by other users only
- [ ] Bookings by current user only
- [ ] Past (not available)

- [] Maybe reveal data.monthly_reservation_fees.desk_allocations for transparency

 -->

{#snippet booking_instructions()}
  <div class='flex-row gap-x g-4 items-center'>
    <Icon name='plus' />
    <p class='text-caption leading-trim'>{@html t('click_any_date_to_book')}</p>
  </div>
  <div class='flex-row gap-x g-4 items-center'>
    <Icon name='check' />
    <p class='text-caption leading-trim'>{t('booking_confirm_below')}</p>
  </div>
  <div class='flex-row gap-x g-4 items-center'>
    <Icon name='person-2' />
    <p class='text-caption leading-trim'>{@html t('book_multiple_desks_per_team_member')}</p>
  </div>
  <div class='flex-row gap-x g-4 items-center'>
    <Icon name='minus' />
    <p class='text-caption leading-trim'>{@html t('cancel_up_to_1_day_before_your_reservation')}</p>
  </div>
{/snippet}


{#if ['instructions'].includes(dialog?.mode)}
  <Dialog onclose={on_close_dialog} class='drawer border-md'>
    {#if dialog?.mode === 'instructions'}
      <div class='flex-column gap-y g-6 px py p-6 mbe m-4'>
        <div class='flex-row items-center gap-x g-7 justify-between'>
          <p class='text-heading3'>{t('how_to_book_a_desk')}</p>
          <button onclick={on_close_dialog}>
            <ButtonLabel variant='brand outlined' start_icon='close' />
          </button>
        </div>
        <hr />
        {@render booking_instructions()}
      </div>
    {/if}
  </Dialog>
{/if}

{#snippet footer(screen_size)}
  {@const is_mobile = screen_size === 'mobile'}
  <!-- On mobile the booking sheet takes the footer's place while something is selected -->
  <footer class='{screen_size} flex-column' class:covered={is_mobile && (summary || selection.receipt)}>
    <div class='footer-card flex-column {is_mobile ? 'border-lg border-1 shadow-md overflow-hidden' : ''}'>
      <div class='footer-header flex-column gap-y g-4 pbe p-6'>
        <!-- Clickable header to expand/collapse the booking details -->
        <svelte:element 
          this={is_mobile ? 'button' : 'div'} 
          class='flex-column pbs px p-6' 
          onclick={(e) => {
            e.preventDefault();
            if(is_mobile) {
              expand_resource_booking_details = !expand_resource_booking_details
            }
          }}
          role={is_mobile ? 'button' : ''}
        >
          <div class='flex-{is_mobile ? 'row justify-between items-center' : 'column'} gap-x g-3 full-width'>
            <div class='flex-row items-center gap-x g-3'>
              {#if is_mobile && data.current_user}
                <Icon name={expand_resource_booking_details ? 'arrow-up' : 'arrow-down'} />
              {/if}
              <h1 class='text-heading3'>{data.resource.title}</h1>
            </div>
          </div>
        </svelte:element>
      
        <p class='text-body px p-6'>
          {#if data.pricing}
            {@const price_info = extract_price_info(data.pricing, 'daily')}
            {@html format_price_info(price_info, t)}
            {#if price_info.price > 0}
              <span class='text-3 text-caption'>{t('plus_vat')}</span>
            {/if}
          {:else}
            {t('unavailable_for_booking')}
          {/if}
        </p>

        <div class='px p-6'>
          {#if data.current_user}
            <p class='text-body text-left mbe m-2'>
              {data.current_user.role === 'admin' ? 'Intern (keine Abrechnung)' : t('booking_on_invoice_of')}
            </p>
            {#key data.current_user.user_id}
              <AccountSwitcher container='dropdown' width='100%' />
            {/key}
          {:else}
            <a href="/login?return_to=book/{data.resource.slug}-{data.resource.entry_id}">
              <ButtonLabel variant='brand {site_settings.primary_control_surface}'>
                {t('login_and_book')}
              </ButtonLabel>
            </a>
          {/if}
        </div>

        <!-- Same as in HalfHourlyBooking.svelte -->
        {#if data.current_user && !current_user_can_book}
          <div class='flex-column gap-y g-4 px p-6'>
            {#if data.account_switcher_users.length >= 1}
              <Alert variant='brand' icon='warning'>
                  <p class='text-caption'>{@html t('add_missing_payment_details_or_switch_account', {href: '/account/billing'})} </p>
              </Alert>
            {:else}
              {#if data.current_user.has_stripe_payment_method === 0}
                <Alert variant='brand' icon='warning'>
                  <p class='text-caption'>{@html t('add_missing_payment_details', {href: '/account/billing'})} </p>
                </Alert>
              {:else if data.current_user.has_stripe_billing_address === 0}
                <Alert variant='brand' icon='warning'>
                  <p class='text-caption'>{@html t('add_missing_billing_address', {href: '/account/billing'})} </p>
                </Alert>
              {/if}
              {#if !data.account_switcher_users.length}
                <Alert variant='brand'>
                  <p class='text-caption'>{@html t('book_on_behalf_of_company_team_member_hint', {email: data.current_user.email})}</p>
                </Alert>
              {/if}
            {/if}
          </div>
        {/if}
 
        {#if expand_resource_booking_details || !is_mobile}
          <div 
            class='booking-details flex-column gap-y g-5 px p-6 mbs m-6' 
            transition:slide={{ duration: 250}}
          >
            {#if data.pricing}
              {#if data.monthly_reservation_fees.desk_allocations.length > 0}
                <div class='flex-wrap justify-between gap-x g-10'>
                  <p class='text-caption leading-trim'>{t('booking_count')}</p>
                  <p class='text-caption leading-trim'>{desk_allocation_summary.total_desks}×</p>
                </div>
                
                <hr />
                {#each desk_allocation_summary.desk_summaries as summary}
                  <div class='flex-row justify-between gap-x g-10 items-center'>
                    <p class='text-caption leading-trim'>
                      {t('desk')} {summary.desk_number}
                    </p>
                    <div class="flex-row gap-x g-4 items-center">
                      <p class='text-caption leading-trim'>{summary.total_days} {summary.total_days === 1 ? t('day') : t('days')}</p>
                      <Tooltip variant='ghostly' placement='bottom-end' icon='dot-dot-dot'>
                        {#snippet description()}
                        {#each summary.date_ranges as range}
                          <p class='text-caption'>
                            {format_date(range.start, 'MMM D')}
                            {#if range.days > 1}
                              - {format_date(range.end, 'MMM D')}
                            {/if}
                          </p>
                        {/each}
                        {/snippet}
                      </Tooltip>
                    </div>
                  </div>
                {/each}
                <hr />

                <div class='flex-wrap justify-between gap-x g-10 mbe m-10'>
                  <p class='text-caption leading-trim -bold'>{t('total_for_month', {month: format_date(`${data.year}-${data.month}-01`, 'MMMM')})}</p>
                  <RollingNumberDisplay 
                    number={format_price(data.monthly_reservation_fees.amount)}
                    class='text-caption -bold'
                  />
                </div>
              {/if}
            {:else}
              <div class='flex-row gap-x g-4'>
                <!-- current_user is null for logged-out visitors — must not crash during SSR -->
                {#if data.current_user?.role === 'admin'}
                  <Alert icon='warning' class='flex-column gap-y g-3'>
                    <p class='text-caption'>{t('pricing_not_configured')}. {t('unavailable_for_booking')}</p>
                    <a href='/pricing-and-capacity'>
                      <ButtonLabel variant='system filled'>
                        {t('edit')}
                      </ButtonLabel>
                    </a>
                  </Alert>
                {:else}
                  <Alert icon='warning'>
                    <p class='text-caption leading-trim'>{t('unavailable_for_booking')}</p>
                  </Alert>
                {/if}
              </div>
            {/if}

            {#if data.resource.preview_description}
              <p class='text-preamble leading-trim'>{t('good_to_know')}:</p>
              <p class='text-body leading-trim'>{data.resource.preview_description}</p>
            {/if}

          </div>
        {/if}
      </div>
    </div>
    {#if !is_mobile}
      <div class='instructions-desktop-tooltip flex-row justify-end full-width px py p-4'>
        <button onclick={() => {dialog = {mode: 'instructions'}}}>
          <ButtonLabel variant='brand ghostly' start_icon='tooltip-circle-outline'>{t('how_to_book_a_desk')}</ButtonLabel>
        </button>        
      </div>
    {/if}

  </footer>
{/snippet}

<BlockGrid>
  <div class='booking-column'>
  <div class='daily-booking-container flex-column'>
    <header class='flex-column items-center full-width shadow-md'>
      <div class="calendar-header full-width padding-page-inline">
        <div class='row-1 flex-row justify-between'>
          <button class='date-nav-btn previous-month' onclick={() => navigate_adjacent_months('back')} title={t("previous")}>
            <ButtonLabel variant='brand ghostly' start_icon="arrow-left" />
          </button>

          <button onclick={() => {show_month_selector = !show_month_selector}}>
            <ButtonLabel variant='brand ghostly' start_icon="calendar" end_icon={show_month_selector ? 'small-arrow-up' : 'small-arrow-down'}>
              {data.year} {format_date(`${data.year}-${data.month}-01`, 'MMMM')}
            </ButtonLabel>
          </button>

          <button class='date-nav-btn next-month justify-end' onclick={() => navigate_adjacent_months('forward')} title={t("next")}>
            <ButtonLabel variant='brand ghostly' end_icon="arrow-right" />
          </button>
        </div>
      </div>

      {#if !show_month_selector}
        <div class="weekday-legend flex-row justify-between full-width p-y p-4 padding-page-inline py p-3">
          {#each Array.from({length: 7}, (_, i) => {
            return format_date(`2024-01-${i+1}`, 'E') // Using Jan 1, 2024 (a Monday) as base
          }) as day}
            <div class="weekday-item text-caption text-center full-width -bold">{day}</div>
          {/each}
        </div>        
      {/if}
    </header>

    {#if show_month_selector}
      <div class='month-selector-container'>
        <MonthSelector 
          month={data.month}
          on_select={handle_month_select}
        />
      </div>
    {:else}

      <main 
        class="daily-slots-container padding-page-inline content-start"
        use:swipe_navigation={{ on_navigate: navigate_adjacent_months }}
      >
        <!-- Add empty slots at the start of the month to align dates with correct weekdays.
              For example, if the 1st falls on a Wednesday, add 2 empty slots for Monday and Tuesday -->
        {#each Array.from({ length: (new Date(data.slots[0].date + 'T00:00:00Z').getUTCDay() || 7) - 1 }, () => null) as _}
          <div class="daily-slot-item blocked"></div>
        {/each}
        {#each data.slots as slot, index}
          {@const delta = delta_of(slot.date)}
          {@const own = projected_own(slot)}
          {@const going = Math.max(-delta, 0)}
          {@const adding = Math.max(delta, 0)}
          {@const degrees_per_desk = available_quantity ? 360 / available_quantity : 0}
          <div
            class="daily-slot-item {slot.time_state} {slot.business_hours_state}"
            class:blocked={slot.blocked}
            class:booked-by-current-user={slot.bookings_by_current_user > 0 || adding > 0}
            class:fully-booked={slot.fully_booked}
            class:is-admin={data.current_user?.role === 'admin'}
            title={slot.fully_booked ? t('fully_booked') : ''}
          >
            <svelte:element
              this={data.current_user?.role === 'admin' ? 'button' : 'div'}
              class='date-and-capacity'
              bind:this={popover_refs[index]}
              popovertarget='all_bookings_for_selected_day_popover{index}'
            >
              <div 
                class='date-number text-heading2 leading-trim'
              >
                {format_date(slot.date, 'D')}
              </div>
              <!-- From 12 o'clock: others, yours, yours selected for cancellation, selected to book -->
              <div class='occupied-percentage'
                class:has-bookings={slot.bookings_total > 0 || adding > 0}
                style:--others="{(slot.bookings_total - slot.bookings_by_current_user) * degrees_per_desk}deg"
                style:--own="{(slot.bookings_by_current_user - going) * degrees_per_desk}deg"
                style:--going="{going * degrees_per_desk}deg"
                style:--adding="{adding * degrees_per_desk}deg"
              >
                {#if delta !== 0}
                  <div class='selected-segments'></div>
                {/if}
                {#if own > 0 || delta !== 0}
                  <div class='slot-count-on-circle'>
                    <span class='digit text-caption leading-trim' class:selected={delta !== 0}>{own}×</span>
                  </div>
                {/if}
              </div>
            </svelte:element>


            <!-- All bookings for the selected day (for admins only) -->
            {#if data.current_user?.role === 'admin'}
              {@const bookings_of_this_day = data.slots.find(_slot => _slot.date === slot.date)?.bookings || []}
              <Popover 
                anchor={popover_refs[index]}
                id='all_bookings_for_selected_day_popover{index}'
                mode='auto'
                placement="bottom-center"
                offset={20}
              >
                <div class='all-bookings-of-selected-day flex-column gap-y g-3 px py p-4'>
                  {#if bookings_of_this_day.length > 0}
                    <div class='flex-row gap-x g-4 items-center justify-between'>
                      <p class='text-body -bold'>{t('bookings_for_day', {day: format_date(slot.date, 'DMMM')})}</p>
                    </div>
                  {#each bookings_of_this_day as booking}
                    <div class='flex-row gap-x g-4 justify-between'>
                      <div class='flex-column'>
                        <p class='text-caption'>{booking.user_name}</p>
                        <p class='text-caption text-3'>({booking.user_email})</p>
                      </div>
                      <p class='text-caption'>{booking.booking_count}×</p>
                      </div>
                    {/each}
                  {:else}
                    <p class='text-caption'>{t('no_bookings_for_this_day')}</p>
                  {/if}
                </div>
              </Popover>
            {/if}


            {#if data.pricing && !slot.blocked && (!slot.fully_booked || slot.bookings_by_current_user > 0)}
              <button
                class='booking-button increase'
                disabled={!has_free_desk(slot)}
                onclick={() => change_selection(slot, 1)}
                aria-label="{format_date(slot.date, 'EEEdMMM')}: {t('booking_book')}"
              >
                <Icon name='plus' />
              </button>
              {#if own > 0 || delta !== 0}
                <div class='slot-count-between-buttons'>
                  <span class='digit text-caption leading-trim' class:selected={delta !== 0}>{own}×</span>
                </div>
              {/if}
                <button
                  class='booking-button decrease'
                  disabled={!can_decrease(slot)}
                  onclick={() => change_selection(slot, -1)}
                  aria-label="{format_date(slot.date, 'EEEdMMM')}: {t('booking_cancel')}"
                >
                  <Icon name='minus' />
                </button>
            {/if}
          </div>
        {/each}
      </main>
    {/if}

    {@render footer('mobile')}
    {@render footer('desktop')}
  </div>
  <!-- Docks under the calendar; on wide screens the right-hand panel stays free -->
  <div class='sheet-dock'>
    <BookingSheet
      {summary}
      receipt={selection.receipt}
      error={selection.error}
      busy={selection.busy}
      stale={selection.estimate_stale}
      on_commit={commit}
      on_clear={() => selection.clear()}
      on_done={() => selection.dismiss_receipt()}
    />
  </div>
  </div>

</BlockGrid>


<style lang="postcss">
  /* The booking sheet sits at the bottom of this column and is clipped while it slides out.
     Clipped vertically only, so the sheet's shadow is not cut off at the sides. */
  .booking-column {
    grid-column: 1 / -1;
    position: relative;
    overflow: visible clip;
    height: calc(100svh - var(--dynamic-island-bottom-edge) - var(--scrollbar-width));
  }
  .sheet-dock {
    position: absolute;
    inset: 0;
    pointer-events: none;
    @media (min-width: 1281px) {
      right: 350px;
    }
  }
  .daily-booking-container {
    overflow-y: auto;
    overflow-x: clip;
    container-type: inline-size;
    height: 100%;
    overscroll-behavior-block: contain;
    overscroll-behavior-inline: contain;

    @media (min-width: 1281px) {
      display: grid;
      grid-template-columns: 1fr 350px;
      grid-template-rows: auto 1fr;
      grid-template-areas: 
        'header footer'
        'main footer'
      ;
    }
    header {
      clip-path: inset(0px 0px -9999px 0px);
      position: sticky;
      top: 0;
      z-index: 3;
      background-color: var(--color-default-background);
      @media (max-width: 1280px) {
        box-shadow: var(--shadow-md);
      }
      @media (min-width: 1281px) {
        grid-area: header;
      }
    }
    main {
      @media (min-width: 1281px) {
        grid-area: main;
      }
    }
    footer {
      &.mobile {
        @media (min-width: 1281px) {
          display: none;
        }
        padding: var(--size-4);
        position: sticky;
        bottom: 0;
        z-index: 2;
        width: 100%;
        &.covered {
          visibility: hidden;
        }
        /* @media (min-width: 500px) {
          max-width: 500px;
          align-self: end;
        } */
        .footer-card {
          width: 100%;
          background-color: var(--color-default-background);
          .footer-header {
            -webkit-tap-highlight-color: rgba(0,0,0,0);
          }
        }
      }
      &.desktop {
        @media (max-width: 1280px) {
          display: none;
        }
        grid-area: footer;
        box-shadow: var(--shadow-md);
        border-left: 1px solid var(--color-default-stroke);
        padding-inline: 0;
        background-color: var(--color-default-background);
        min-height: max-content;
        position: relative;
        .footer-card {
          position: sticky;
          top: 0;
        }
        /* &.has-pricing {
          background: linear-gradient(120deg, var(--color-default-background-50) 0%, transparent 100%);
        }
        &:not(.has-pricing) {
          background-color: var(--color-default-background);
        } */
        
      }
    }
    .month-selector-container {
      grid-area: main;
    }
  }
  .daily-slots-container {
    display: grid;
    /* grid-template-columns: repeat(auto-fit, minmax(15ch,1fr)); */
    grid-template-columns: repeat(7, 1fr);
    padding-block: var(--size-2);
    /* row-gap: var(--size-4);
    column-gap: var(--size-6); */
    @media (hover: hover) {
      /* &:has(.daily-slot-item:not(.blocked):not(.fully-booked):hover) .daily-slot-item:not(:hover) {
        opacity: 0.4;
      } */
    }
  }
  .daily-slot-item {
    transition: opacity 0.3s ease-in-out;
    display: grid;
    align-items: center;
    justify-items: center;
    position: relative;
    grid-template-areas: 
      'slot-counter slot-counter'
      'date-and-capacity  date-and-capacity'
      'decrease increase'
    ;
    grid-template-rows: var(--touchable-target-min-size) 1fr var(--touchable-target-min-size);
    grid-template-columns: 1fr 1fr;
    @media (min-width: 769px) {
      grid-template-areas: 
        '. date-and-capacity increase'
        '. date-and-capacity slot-counter'
        '. date-and-capacity decrease';
      grid-template-columns: minmax(10px, 1fr) auto minmax(50px, 1fr);
      grid-template-rows: var(--touchable-target-min-size) 26px var(--touchable-target-min-size);
    }
    >* {
      width: 100%;
    }
    &.past {
      .date-and-capacity .date-number{
        opacity: 0.5;
      }
    }
    &.fully-booked:not(.booked-by-current-user), 
    &.outside-business-hours:not(.booked-by-current-user),
    &.past:not(.booked-by-current-user) {
      .date-and-capacity .date-number{
        opacity: 0.2;
      }
    }
    &.fully-booked:not(.booked-by-current-user), 
    &.outside-business-hours:not(.booked-by-current-user) {
      .occupied-percentage:after {
      
        --stroke-width: 2.42px; 
        --stripe: var(--color-default-background-50);
        --angle: 45deg;
        background: repeating-linear-gradient(
          var(--angle),
          var(--stripe) 0,
          var(--stripe) var(--stroke-width),
          transparent var(--stroke-width),
          transparent calc(2 * var(--stroke-width))
          );
      }
    }
    .booking-button {
      height: 100%;
      width: 100%;
      min-width: 0;
      min-height: var(--touchable-target-min-size);
      border-radius: 100%;
      display: flex;
      flex-direction: column;
      justify-content: center;
      gap: var(--size-3);
      user-select: none;
      &:disabled {
        opacity: 0.2;
        pointer-events: none;
      }
    }
    
    .decrease {
      grid-area: decrease; 
      align-items: center;
      justify-content: center;
      @media (min-width: 769px) {
        justify-content: flex-start;
      }
    }
    .date-and-capacity {
      user-select: none;
      min-width: fit-content;
      text-align: center;
      z-index: 1;
      grid-area: date-and-capacity;
      position: relative;
      &:not(button) {
        pointer-events: none;
      }
      .text-heading2 {
        @media (min-width: 768px) and (max-width: 1400px) {
          --size-modifier: 0.8;
        }
        @media (min-width: 1401px) {
          --size-modifier: 0.9;
        }
      }
    }
    &:has(.date-and-capacity:focus) {
      /* outline: 2px solid var(--color-default-background-50); */
      box-shadow: inset 0 0 4px var(--color-default-background-50), inset 0 0 10px var(--color-default-background-50);
    }


    .increase {
      position: relative;
      grid-area: increase; 
      align-items: center;
      justify-content: center;
      @media (min-width: 769px) {
        justify-content: flex-end;
        flex-direction: column;
      }
    }
    .slot-count-between-buttons {
      grid-area: slot-counter;
    }
    .slot-count-between-buttons, .slot-count-on-circle {
      opacity: 1;
      transition: opacity 0.3s ease-in-out;
      display: flex;
      justify-content: center;
      .digit {
        aspect-ratio: 1;
        padding: var(--size-2);
        border-radius: 100%;
        display: flex;
        align-items: center;
        justify-content: center;
        user-select: none;
        /* aspect-ratio only applies with an automatic height; the min-width keeps "1×" a circle, not an upright ellipse */
        width: fit-content;
        min-width: calc(1lh + 2 * var(--size-2));
        align-self: center;
        &.selected {
          background: var(--color-default-background);
          box-shadow: inset 0 0 0 1px var(--color-default-foreground);
        }
      }
    }

    .occupied-percentage {
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      z-index: -1;
      border-radius: 100%;
      aspect-ratio: 1;
      width: auto;
      height: 180%;
      &.has-bookings {
        outline: 1px solid var(--color-default-stroke);
        outline-offset: -1px;
        /* background-color: var(--color-default-background-50); */
      }
      &:before, &:after {
        border-radius: 100%;
        content: '';
        position: absolute;
        inset: 0;
      }
      /* From 12 o'clock: booked by others, then by the current user, then what is selected, so a new
         booking grows the member's segment where it will stay */
      --own-end: calc(var(--others) + var(--own));
      --going-end: calc(var(--own-end) + var(--going));
      --member-end: calc(var(--going-end) + var(--adding));
      /* booked by current user */
      &:before {
        background: var(--color-default-emphasis-background);
        outline: 2px solid oklch(from var(--color-default-foreground) l c h / 0.3);
        outline-offset: -2px;
        mask-image: conic-gradient(
          transparent 0deg,
          transparent var(--others),
          black var(--others),
          black var(--own-end),
          transparent var(--own-end)
        );
      }
      /* booked by others */
      &:after {
        background: var(--color-default-stroke);
        outline: 2px solid oklch(from var(--color-default-foreground) l c h / 0.1);
        outline-offset: -2px;
        mask-image: conic-gradient(
          black 0deg,
          black var(--others),
          transparent var(--others)
        );
      }
      /* selected: own desks to cancel in red tint, desks to book in lime */
      .selected-segments {
        position: absolute;
        inset: 0;
        border-radius: 100%;
        background: conic-gradient(
          transparent 0deg,
          transparent var(--own-end),
          color-mix(in oklab, var(--color-destructive) 35%, var(--color-default-background)) var(--own-end),
          color-mix(in oklab, var(--color-destructive) 35%, var(--color-default-background)) var(--going-end),
          oklch(from var(--color-default-emphasis-background) l c h / 0.6) var(--going-end),
          oklch(from var(--color-default-emphasis-background) l c h / 0.6) var(--member-end),
          transparent var(--member-end)
        );
      }
      .slot-count-on-circle {
        transition: all 0.3s cubic-bezier(0.68, -0.2, 0.265, 1.2);
        opacity: 0;
        pointer-events: none;
        position: absolute;
        height: 200%;
        left: -40%;
        top: -40%;
        aspect-ratio: 1;
        z-index: 2;
        .digit {
          background: var(--color-default-emphasis-background);
        }
      }
    }
    @media (hover: hover) {
      &:not(.is-admin):hover, &.past {
        .slot-count-between-buttons {
          opacity: 0;
        }
        .slot-count-on-circle {
          opacity: 1;
          rotate: var(--member-end);
          .digit {
            rotate: calc(-1 * var(--member-end));
          }
        }
      }
    }
  }

  .daily-slots-container:has(.daily-slot-item .date-and-capacity:focus) .daily-slot-item:not(:has(.date-and-capacity:focus)) {
    /* opacity: 0.2; */
  }

  .instructions-desktop-tooltip {
    margin-block-start: auto;
  }
</style>